import { Switch, Route, Redirect, useLocation } from "react-router-dom";
import {
  IonRouterOutlet,
  IonTabs,
  IonTabBar,
  IonTabButton,
  IonLabel,
} from "@ionic/react";

import {
  IconMessageCircle,
  IconOutbound,
  IconLayoutDashboard,
  IconMenu2,
  IconBrain,
} from "@tabler/icons-react";

import { FormattedMessage } from "react-intl";

import Chats from "./mobile/Chats";
import Chat from "./mobile/Chat";
import ContactDetail from "./mobile/ContactDetail";
import Settings from "./mobile/Settings";

import Language from "./mobile/Language";
import Team from "./mobile/Team";
import Integrations from "../../components/integrations";
import Payment from "./mobile/Payment";
import Usage from "./mobile/Usage";
import UpdateAppVersion from "./mobile/UpdateAppVersion";

// Leads
import Payments from "../../components/payments";

// Components
import OutboundLogs from "../../components/outbound/logs/mobile";
import Home from "../../components/home";
import Outbound from "../../components/outbound";
import NavBanner from "../../components/NavBanner";
import AccountNewInvitation from "../../components/account/accept-invitation";
import BehaviorMobile from "../../components/ai/settings/instructions/mobile";
//import MobilePlayGround from "../../components/ai/mobile";
import AIHome from "../../components/ai/mobile-home";
import AiSystemPrompt from "../../components/system-prompt";
import AiEscalations from "../../components/ai/escalations";
import AiFollowUps from "../../components/ai/follow-ups";
import PlaygroundBehavior from "../../components/ai/playground";
//import FreeTextMobile from "../../components/ai/free-text/mobile";

import CrawlWebsite from "../../components/ai/website";
import FreeText from "../../components/ai/free-text";

import Files from "../../components/ai/files";
import ProtectedRoute from "../../guards/role-route-guard";

// import Instructions from "../../components/ai/settings/instructions";
import AiLayout from "../../components/ai/layout";

import { useAppStore } from "../../store/app";
import useCountersStore from "../../store/counts";

const normalizePathname = (pathname: string) => {
  if (!pathname) return "/";
  return pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
};

const isWelcomePath = (pathname: string) => {
  const normalizedPath = normalizePathname(pathname);
  return (
    normalizedPath === "/welcome" ||
    normalizedPath === "/ai/playground/welcome"
  );
};

function OnboardingGuard({
  isOnboarded,
  welcomeCompleted,
  paymentActive,
  role,
  isLoading,
  children,
}: {
  isLoading: boolean;
  isOnboarded: boolean;
  welcomeCompleted: boolean;
  paymentActive: boolean;
  role: string;
  children: React.ReactNode;
}) {
  const location = useLocation();
  const canAccessAi = role === "admin" || role === "system";
  const ALLOWD_PATHS = [canAccessAi ? "/ai" : "/home"];
  const isWelcomeRoute = isWelcomePath(location.pathname);

  if (
    canAccessAi &&
    !isLoading &&
    isOnboarded &&
    !paymentActive &&
    !welcomeCompleted &&
    !isWelcomeRoute
  ) {
    return <Redirect to="/welcome" />;
  }

  if (
    canAccessAi &&
    !isLoading &&
    isOnboarded &&
    (welcomeCompleted || paymentActive) &&
    isWelcomeRoute
  ) {
    return <Redirect to="/ai" />;
  }

  if (!isOnboarded && !isLoading && location.pathname.startsWith("/ai")) {
    const ok = ALLOWD_PATHS.some(
      (p) => location.pathname === p || location.pathname.startsWith(`${p}/`),
    );
    if (!ok) return <Redirect to={ALLOWD_PATHS[0]} />;
  }
  return <>{children}</>;
}

const MobileHome: React.FC<ILayoutProps> = (props: ILayoutProps) => {
  const { onRequestSettings, refreshUser, switchCompany } = props;
  const location = useLocation();
  const { pathname } = location;
  const tabs = [
    "/home",
    "/chats",
    "/outbound",
    "/settings",
    "/language",
    "/ai",
    "/menu",
    "/account/accept-invitation",
  ];

  const settings = useAppStore((state) => state.settings);
  const user = useAppStore((state) => state.user);
  const total = useCountersStore((state) => state.total);
  const isOnboarded = settings.onboarded;
  const welcomeCompleted = settings.welcomeCompleted;
  const paymentActive = Boolean(settings.status?.payment?.active);
  const isOnboardingTransitioning =
    isOnboarded &&
    (settings.onboarding.loading || settings.onboarding.creatingAgent);

  const hide = !tabs.includes(pathname) || !isOnboarded || isOnboardingTransitioning;
  const tabBarStyle = hide || !isOnboarded ? { display: "none" } : undefined;

  const isAdmin = user.role === "admin" || user.role === "system";

  return (
    <>
      <NavBanner settings={settings} mode={"mobile"} hide={hide} />
      <OnboardingGuard
        isOnboarded={isOnboarded}
        welcomeCompleted={welcomeCompleted}
        paymentActive={paymentActive}
        role={user.role}
        isLoading={settings.loading}
      >
        <IonTabs>
          <IonRouterOutlet>
            <Route
              exact
              path="/welcome"
              render={() => {
                if (settings.loading) return null;
                if (!(user.role === "admin" || user.role === "system")) {
                  return <Redirect to="/home" />;
                }
                if (!isOnboarded) return <Redirect to="/ai" />;
                return (
                  <PlaygroundBehavior
                    isolated={true}
                    onSwitchCompany={switchCompany}
                  />
                );
              }}
            />
            <Route
              exact
              path="/ai/playground/welcome"
              render={() => <Redirect to="/welcome" />}
            />
            <Route exact path="/home" render={() => <Home />} />
            <Route exact path="/chats" render={() => <Chats />} />
            <Route
              exact
              path="/outbound"
              render={() => <Outbound settings={settings} />}
            />
            <Route path="/outbound/:id" component={OutboundLogs} />

            <Route
              exact
              path="/settings"
              render={() => (
                <Settings
                  user={user}
                  settings={settings}
                  switchCompany={switchCompany}
                />
              )}
            />

            <Route path="/chat/:id/contact" component={ContactDetail} />
            <Route
              exact
              path="/chat/:id"
              render={(routeProps) => (
                <Chat {...routeProps} settings={settings} />
              )}
            />

            <Route
              exact
              path="/chats/:id"
              render={(routeProps) => (
                <Chat {...routeProps} settings={settings} />
              )}
            />

            <ProtectedRoute
              role={user.role}
              path="/integrations"
              component={Integrations}
            />
            <ProtectedRoute
              role={user.role}
              path="/connect"
              component={Integrations}
            />
            <ProtectedRoute
              role={user.role}
              exact
              path="/payments/settings"
              render={() => <Payments settings={settings} />}
            />
            <ProtectedRoute
              role={user.role}
              exact
              path="/payments/stripe"
              render={() => <Payments settings={settings} />}
            />
            <ProtectedRoute
              role={user.role}
              exact
              path="/payments/booking"
              render={() => <Payments settings={settings} />}
            />
            <ProtectedRoute
              role={user.role}
              exact
              path="/payments/items"
              render={() => <Payments settings={settings} />}
            />
            <ProtectedRoute
              role={user.role}
              exact
              path="/payments"
              render={() => <Payments settings={settings} />}
            />

            <ProtectedRoute role={user.role} exact path="/ai">
              <AIHome />
            </ProtectedRoute>

            <ProtectedRoute
              role={user.role}
              exact
              path="/ai/behavior"
              component={BehaviorMobile}
            />

            <ProtectedRoute
              role={user.role}
              exact
              path="/ai/website"
              render={() => (
                <AiLayout>
                  <CrawlWebsite />
                </AiLayout>
              )}
            />

            <ProtectedRoute
              role={user.role}
              exact
              path="/ai/playground"
              render={() => (
                <AiLayout>
                  <PlaygroundBehavior onSwitchCompany={switchCompany} />
                </AiLayout>
              )}
            />
            <ProtectedRoute
              role={user.role}
              path="/ai/text"
              render={() => (
                <AiLayout>
                  <FreeText />
                </AiLayout>
              )}
            />

            <ProtectedRoute
              role={user.role}
              exact
              path="/ai/files"
              render={() => (
                <AiLayout>
                  <Files />
                </AiLayout>
              )}
            />

            <ProtectedRoute
              role={user.role}
              exact
              path="/ai/escalations"
              render={() => (
                <AiLayout>
                  <AiEscalations />
                </AiLayout>
              )}
            />
            <ProtectedRoute
              role={user.role}
              exact
              path="/ai/follow-ups"
              render={() => (
                <AiLayout>
                  <AiFollowUps />
                </AiLayout>
              )}
            />
            <ProtectedRoute
              role={user.role}
              exact
              path="/ai/system-prompt"
              render={() => (
                <AiLayout>
                  <AiSystemPrompt />
                </AiLayout>
              )}
            />
            <Route
              exact
              path="/settings/language"
              render={() => <Language />}
            />

            <ProtectedRoute
              role={user.role}
              exact
              path="/settings/team"
              render={() => (
                <Team
                  user={user}
                  settings={settings}
                  onRequestSettings={onRequestSettings}
                />
              )}
            />

            <ProtectedRoute
              role={user.role}
              exact
              path="/settings/payment"
              render={() => (
                <Payment
                  user={user}
                  settings={settings}
                  onRequestSettings={onRequestSettings}
                />
              )}
            />

            <ProtectedRoute
              role={user.role}
              exact
              path="/settings/usage"
              render={() => (
                <Usage
                  user={user}
                  settings={settings}
                  onRequestSettings={onRequestSettings}
                />
              )}
            />

            <Route
              exact
              path="/account/accept-invitation"
              render={(routeProps) => (
                <AccountNewInvitation
                  {...routeProps}
                  refreshUser={refreshUser}
                />
              )}
            />

            <Route exact path="/" render={() => <Redirect to="/chats" />} />
            <Route path="/download-new-version" component={UpdateAppVersion} />
          </IonRouterOutlet>

          {/* Tab Bar Navigation */}

          <IonTabBar
            slot="bottom"
            style={{
              ...tabBarStyle,
              height: total > 0 ? "55px" : "50px",
            }}
          >
            <IonTabButton className="visito-tabs" tab="home" href="/home">
              <IconLayoutDashboard size={26} />
              <IonLabel>
                {" "}
                <FormattedMessage id="nav.home" />
              </IonLabel>
            </IonTabButton>
            <IonTabButton className="visito-tabs" tab="chats" href="/chats">
              <span className="tab-icon-wrap">
                <IconMessageCircle size={26} />
                {pathname.includes("/chats") || total === 0 ? null : (
                  <span className="notif-pill-mobile abs btn-visito-danger rounded-pill fw-bold">
                    {total > 99 ? "99+" : total}
                  </span>
                )}
              </span>
              <IonLabel>
                {" "}
                <FormattedMessage id="nav.inbox" />
              </IonLabel>
            </IonTabButton>
            {/*
          <IonTabButton className="visito-tabs" tab="ai" href="/ai">
            <IconBrain size={26} />
            <IonLabel>
              <FormattedMessage id="nav.ai" />
            </IonLabel>
          </IonTabButton>
          */}

            {settings.events ? (
              <IonTabButton
                className="visito-tabs"
                tab="outbound"
                href="/outbound"
              >
                <IconOutbound size={26} />
                <IonLabel>
                  {" "}
                  <FormattedMessage id="nav.outbound" />
                </IonLabel>
              </IonTabButton>
            ) : null}
            {isAdmin && (
              <IonTabButton className="visito-tabs" tab="ai" href="/ai">
                <IconBrain size={26} />
                <IonLabel>
                  <FormattedMessage id="nav.ai" />
                </IonLabel>
              </IonTabButton>
            )}
            <IonTabButton className="visito-tabs" tab="menu" href="/settings">
              <IconMenu2 size={26} />
              <IonLabel>
                <FormattedMessage id="nav.menu" />
              </IonLabel>
            </IonTabButton>
          </IonTabBar>
        </IonTabs>
      </OnboardingGuard>
    </>
  );
};

interface ILayoutProps {
  onRequestSettings: Function;
  refreshUser: Function;
  switchCompany: Function;
}

export default MobileHome;
